/**
 * Cache Statistics Module - IPHUB Project
 * Statistics tracking and management for cache operations
 */

/**
 * Cache statistics object
 */
let cacheStats = {
  hits: 0,
  misses: 0,
  sets: 0,
  deletes: 0,
  cleanups: 0,
  errors: 0,
};

/**
 * Get current statistics
 * @returns {Object} Current statistics
 */
export function getStats() {
  return { ...cacheStats };
}

/**
 * Increment hit counter
 */
export function incrementHits() {
  cacheStats.hits++;
}

/**
 * Increment miss counter
 */
export function incrementMisses() {
  cacheStats.misses++;
}

/**
 * Increment set counter
 */
export function incrementSets() {
  cacheStats.sets++;
}

/**
 * Increment delete counter
 */
export function incrementDeletes() {
  cacheStats.deletes++;
}

/**
 * Increment cleanup counter
 */
export function incrementCleanups() {
  cacheStats.cleanups++;
}

/**
 * Increment error counter
 */
export function incrementErrors() {
  cacheStats.errors++;
}

/**
 * Reset all statistics
 */
export function resetStats() {
  cacheStats = {
    hits: 0,
    misses: 0,
    sets: 0,
    deletes: 0,
    cleanups: 0,
    errors: 0,
  };
}

/**
 * Calculate hit rate percentage
 * @returns {string} Hit rate as percentage string
 */
export function calculateHitRate() {
  const totalRequests = cacheStats.hits + cacheStats.misses;
  const hitRate = totalRequests > 0 ? (cacheStats.hits / totalRequests) * 100 : 0;
  return hitRate.toFixed(2) + "%";
}

/**
 * Get detailed statistics with calculated values
 * @param {Object} additionalStats - Additional statistics to include
 * @returns {Object} Detailed statistics
 */
export function getDetailedStats(additionalStats = {}) {
  const totalRequests = cacheStats.hits + cacheStats.misses;
  const hitRate = totalRequests > 0 ? (cacheStats.hits / totalRequests) * 100 : 0;

  return {
    ...cacheStats,
    ...additionalStats,
    totalRequests,
    hitRate: hitRate.toFixed(2) + "%",
    hitRateDecimal: hitRate / 100,
  };
}

/**
 * Export statistics for external use
 * @returns {Object} Statistics object
 */
export function exportStats() {
  return getStats();
}

/**
 * Import statistics from external source
 * @param {Object} stats - Statistics to import
 */
export function importStats(stats) {
  if (stats && typeof stats === "object") {
    cacheStats = {
      hits: stats.hits || 0,
      misses: stats.misses || 0,
      sets: stats.sets || 0,
      deletes: stats.deletes || 0,
      cleanups: stats.cleanups || 0,
      errors: stats.errors || 0,
    };
  }
}
